/**
 * 존 편집기 공용 상수 — 캔버스(ZoneEditor)와 속성 패널(ZoneObjectPanel)이 **같은 정의**를 본다.
 * 두 벌로 두면 타입 아이콘·색이 화면마다 달라진다.
 */

import { ZONES as SHARED_ZONES, ZONE_TITLE, getProp, type TiledObject } from "@greg-chick/shared";

export interface ImgOpt { key: string; url: string }
/**
 * 🔴 **구역 목록·이름은 shared 한 곳**(`zoneName.ts`)에서 가져온다.
 *
 * 예전엔 여기와 웹과 집계 화면에 **세 벌**이 손으로 적혀 있었다. 집계 쪽이 없는 구역
 * `library` 를 갖고 있어서 운영 화면이 「도서관 2 · book 31」로 보였다 — 사실은 한 곳이다.
 * 목록도 마찬가지다: 구역이 하나 늘어도 여기 안 적으면 **편집기 목록에만 안 나타난다.**
 */
export const ZONES: readonly string[] = SHARED_ZONES;
export const ZONE_LABEL: Record<string, string> = ZONE_TITLE;
export const OBJ_TYPES = ["card", "link", "video", "portal", "npc", "store", "decor"];
export const COLOR: Record<string, string> = {
  portal: "#5b8def", npc: "#f4a93b", video: "#e8743b",
  link: "#3fae7a", card: "#9b6dd6", store: "#d6486d", decor: "#8a7a5a",
};
export const TYPE_META: Record<string, { label: string; icon: string }> = {
  card: { label: "카드", icon: "🃏" },
  link: { label: "링크", icon: "🔗" },
  video: { label: "영상", icon: "🎬" },
  portal: { label: "포털", icon: "🌀" },
  npc: { label: "NPC", icon: "🧍" },
  store: { label: "상점", icon: "🛍️" },
  decor: { label: "장식", icon: "🏠" },
};

/** 타입 코드 → 사람이 읽는 이름. 모르는 타입은 코드를 그대로 보여준다. */
export const typeLabel = (t: string | undefined) => (t ? TYPE_META[t]?.label ?? t : "");

// ── 오브젝트에 붙일 그림 고르기 ────────────────────────────────
/**
 * **놓을 수 있는 그림만** 추려서, **소품을 앞에** 둔다.
 *
 * ## 왜 필요한가(실측)
 * 예전엔 매니페스트 전체(262개)를 그대로 보여 주고 앞 60개만 그렸다. 그 60개가
 * `ar 16 · avatar 3 · book 41` 이라 **소품(decor) 18개는 한 개도 안 보였다** — 소품을 고르는
 * 화면인데 소품이 없고, 검색창에 `decor` 를 쳐야만 나왔다.
 * 게다가 **소리 파일 37개**(`sound.*`·`book.audio.*`)가 섞여 깨진 썸네일로 자리를 차지했다.
 * 오브젝트에 mp3 를 붙일 수도 있었는데, 붙이면 아무것도 안 그려진다.
 */

/** 소리·동영상은 그림이 아니다 — 목록에 있으면 깨진 칸이 되고, 고르면 아무것도 안 나온다. */
export function isPlaceableImage(key: string, url: string): boolean {
  if (/\.(mp3|wav|ogg|m4a|mp4|webm|mov|mind)$/i.test(url)) return false;
  if (key.startsWith("sound.") || key.includes(".audio.") || key.startsWith("ar.target.")) return false;
  return true;
}

/** 고르기 탭. `all` 은 도피처 — 그래도 소품이 앞에 오게 정렬한다. */
export const IMG_TABS = [
  { id: "decor", label: "🌳 소품", prefix: "decor." },
  { id: "npc", label: "🐣 친구", prefix: "npc." },
  { id: "all", label: "전체", prefix: "" },
] as const;
export type ImgTabId = (typeof IMG_TABS)[number]["id"];

/**
 * 「전체」에서의 앞뒤 순서. 체험공간에 **놓을 법한 것**이 앞이다.
 * 히어로 파츠(106개)는 아이 옷이라 여기 놓을 일이 없어 맨 뒤로 민다.
 */
const ALL_ORDER = ["decor.", "npc.", "world.", "floor.", "landing.", "goods.", "ar.", "book.", "avatar.", "hero."];
const orderOf = (key: string): number => {
  const i = ALL_ORDER.findIndex((p) => key.startsWith(p));
  return i < 0 ? ALL_ORDER.length : i;
};

/** 이 탭에서 보여줄 목록(검색어 반영). 정렬까지 끝난 상태로 돌려준다. */
export function zoneImageOptions(images: ImgOpt[], tab: ImgTabId, query = ""): ImgOpt[] {
  const q = query.trim().toLowerCase();
  const prefix = IMG_TABS.find((t) => t.id === tab)?.prefix ?? "";
  return images
    .filter((i) => isPlaceableImage(i.key, i.url))
    .filter((i) => (prefix ? i.key.startsWith(prefix) : true))
    .filter((i) => (q ? i.key.toLowerCase().includes(q) : true))
    .sort((a, b) => orderOf(a.key) - orderOf(b.key) || a.key.localeCompare(b.key));
}

/**
 * 🏷️ **이름이 같은 물건은 자리로 가른다** (2026-10-13).
 *
 * 그림책 마을에는 이벤트 광장으로 가는 문이 둘(계단 문·옆문)인데 둘 다 「이벤트 광장」이라, 옆문을 더한
 * 운영자가 「이벤트 광장이 2개가 됐다」며 하나를 지웠다. 어느 것을 지웠는지 화면만 봐서는 알 수 없었다.
 * 겹칠 때만 자리를 붙인다 — 평소 이름표는 그대로.
 */
export function objectLabel(objects: readonly TiledObject[], o: TiledObject, tile: number): string {
  const title = String(getProp(o, "title_ko") || o.name || "");
  const same = objects.filter((x) => String(getProp(x, "title_ko") || x.name || "") === title);
  if (same.length < 2) return title;
  return `${title} · 가로 ${Math.round(o.x / tile) + 1}·세로 ${Math.round(o.y / tile) + 1}`;
}

/**
 * 🚪 **어느 구역으로 가는 마지막 문을 지우려 하나** — 지우면 아이가 그 구역에 못 간다.
 * 같은 곳으로 가는 문이 하나 더 있으면 묻지 않는다(옆문·계단 문처럼 둘이면 하나는 지워도 길은 남는다).
 */
export function lastDoorWarning(objects: readonly TiledObject[], o: TiledObject): string | null {
  if (o.type !== "portal") return null;
  const to = getProp(o, "to");
  if (typeof to !== "string" || !to) return null;
  const others = objects.filter((x) => x !== o && x.name !== o.name && x.type === "portal" && getProp(x, "to") === to);
  if (others.length) return null;
  const where = ZONE_TITLE[to as keyof typeof ZONE_TITLE] ?? to;
  return `이 문이 「${where}」(으)로 가는 마지막 문이에요.\n지우고 웹에 올리면 아이가 ${where}에 갈 수 없어요. 지울까요?`;
}

/**
 * 🧺 **올리면 게시본에서 빠지는 물건** — 불러온 판(게시본)에는 있었는데 지금 올리려는 판에는 없는 것 (2026-10-13).
 *
 * 편집기는 초안을 창이 닫혀도 기억한다. 며칠 전 지운 것이 초안에 남은 채로 다른 일로 [웹에 올리기]를 누르면
 * **묻지 않고** 아이 화면에서 사라진다(옆문을 더하다 문 하나를 지운 2026-10-13 의 모양). 올리기 직전에 이름으로 보여 준다.
 * 이름은 불러온 판 기준으로 가른다 — 지운 것은 지금 판에 없으니까.
 */
export function removedOnPublish(baseline: unknown, current: { layers?: unknown } | null, tile: number): string[] {
  const objsOf = (m: unknown): TiledObject[] => {
    const layers = (m as { layers?: unknown } | null)?.layers;
    return (Array.isArray(layers) ? layers : []).flatMap((l) => {
      const o = (l as { objects?: unknown } | null)?.objects;
      return Array.isArray(o) ? (o as TiledObject[]) : [];
    });
  };
  const before = objsOf(baseline);
  const now = new Set(objsOf(current).map((o) => o.name));
  return before.filter((o) => o.name && !now.has(o.name)).map((o) => `${typeLabel(o.type)} 「${objectLabel(before, o, tile)}」`);
}
